import * as React from 'react';
import Highcharts from 'highcharts';

declare module 'highcharts' {
  interface Chart {
    highlight?: (
      this: Highcharts.Chart,
      e: PointerEvent | MouseEvent | TouchEvent
    ) => void;
  }

  interface Point {
    distX?: number;
  }
  interface Options {
    synchronize?: { enabled?: boolean };
  }
}

type SynchronizedInstancesEventHandlers = {
  synchronizeInstanceExtremes: (
    this: Highcharts.Axis,
    e: Highcharts.AxisSetExtremesEventObject
  ) => void;
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Synchronized Module
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const nonNullable = (a: unknown) => typeof a !== 'undefined' && a !== null;

const SynchronizedInstances = (H: typeof Highcharts): void => {
  H.Pointer.prototype.reset = function () {
    return undefined;
  };

  H.Chart.prototype.highlight = function (e) {
    // Normalize the browser event in order to get missing properties (chartX, chartY).
    const event = this.pointer.normalize(e);

    // Finds the nearest point on each series to where the mouse's X position is.
    // If no point is found withing threshold, undefined will be returned.
    // If a point is found, it may or may not be the same across series.
    let points = this.series?.map((s) => s.searchPoint(event, true)) ?? [];
    points = points.filter(nonNullable);
    if (!points.length) return;

    // Find the closest point.
    const closest = points.reduce((c, p) => {
      if (!nonNullable(p.distX)) return c;
      if (!nonNullable(c.distX)) return p;
      return p.distX < c.distX ? p : c;
    }, points[0]);
    if (!nonNullable(closest.distX)) return;

    // Keep only the ones with the same distance.
    // This will remove findings from searchPoint that are not exactly the same as the others.
    // This behavior only works on overlay mode, on stack, each instance has only 1 series so information about the
    // rest of the series is lost.
    points = points.filter((p) => p.distX === closest.distX);

    // Render the tooltip and marker for each of the points that passed all checks.
    points.forEach((p) => p.onMouseOver(event));
    this.tooltip.refresh(points, event);

    // Render the crosshair over the closest point.
    const axis = this.xAxis[0];
    if (axis) axis.drawCrosshair(event, closest);
  };
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Synchronized Hook
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const synchronizedEventTypes = [
  'mousemove',
  'touchmove',
  'touchstart',
] as const;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useSynchronizedInstances = (
  containerElementRef: React.RefObject<HTMLDivElement>,
  instancesMapRef: React.MutableRefObject<Map<string, Highcharts.Chart>>
) => {
  const synchronizeInstanceExtremes = React.useCallback(function (
    this: Highcharts.Axis,
    e: Highcharts.AxisSetExtremesEventObject
  ): void {
    if (
      e.trigger !== undefined &&
      !e.trigger.startsWith('sync') &&
      this.chart.options.synchronize?.enabled
    ) {
      instancesMapRef.current.forEach((instance) => {
        if (
          instance &&
          instance !== this.chart &&
          instance.options.synchronize?.enabled
        ) {
          if (e.trigger === 'pan') {
            instance.tooltip?.hide?.(0);
            instance.xAxis[0]?.hideCrosshair?.();
            instance.series?.forEach(function (series) {
              series.setState('normal');
              series.points?.forEach((point) => {
                point.setState?.('normal');
              });
            });
          }

          instance.xAxis[0]?.setExtremes?.(e.min, e.max, undefined, false, {
            trigger: `sync:${e.trigger}`,
            triggerOp: e.trigger,
            source: this.chart,
          });
        }
      });
    }
  }, []);

  React.useLayoutEffect(() => {
    const synchronizedEventsHandler = (
      event: PointerEvent | MouseEvent | TouchEvent
    ) => {
      instancesMapRef.current.forEach((instance) => {
        if (!instance?.options || !instance?.pointer) return;
        instance.highlight(event);
      });
    };

    synchronizedEventTypes.forEach((eventType) => {
      containerElementRef.current?.addEventListener(
        eventType,
        synchronizedEventsHandler
      );
    });

    return () => {
      synchronizedEventTypes.forEach((eventType) => {
        containerElementRef.current?.removeEventListener(
          eventType,
          synchronizedEventsHandler
        );
      });
    };
  });

  return { synchronizeInstanceExtremes };
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Make options with synchronization events
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const makeOptionsWithSynchronizedEvents = (
  options: Highcharts.Options,
  eventHandlersRef: React.MutableRefObject<SynchronizedInstancesEventHandlers>
): Highcharts.Options => {
  // TODO: This will throw if chart has multiple options.xAxis entries.
  // For now, any of the charts using synchronized instances have multiple
  // X axes. If yours does, you'll need to fix this one.

  return {
    ...options,
    xAxis: {
      ...options.xAxis,
      events: {
        ...(options.xAxis as Highcharts.XAxisOptions)?.events,
        setExtremes: function (event) {
          eventHandlersRef.current.synchronizeInstanceExtremes?.call(
            this,
            event
          );

          (options.xAxis as Highcharts.XAxisOptions)?.events?.setExtremes?.call(
            this,
            event
          );
        },
      },
    },
  };
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export {
  SynchronizedInstances,
  useSynchronizedInstances,
  makeOptionsWithSynchronizedEvents,
};

export type { SynchronizedInstancesEventHandlers };
